import * as RadixDialog from "@radix-ui/react-dialog";
import { PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, NavLink } from "react-router";

import { useNavAllowed } from "@/app/shell/useNavAllowed";
import { NAV_ENTRIES, NAV_GROUPS, type NavEntry } from "@/app/shell/nav";
import { nextSidebarScrollTop } from "@/app/shell/sidebarScroll";
import { sidebarIconTileClass, sidebarRowClass } from "@/app/shell/sidebarStyles";
import { UserMenu } from "@/app/shell/UserMenu";
import { ClockChip } from "@/features/attendance/components/ClockChip";
import { RunningTimer } from "@/features/time/components/RunningTimer";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { MOD_KEY } from "@/shared/lib/platform";
import { Kbd } from "@/shared/ui/Kbd";
import { LogoWordmark } from "@/shared/ui/Logo";
import { Tooltip } from "@/shared/ui/Tooltip";

export interface SidebarActions {
  onSearch: () => void;
  onNewProject: () => void;
  onNavigate?: () => void;
}

interface RailProps {
  /** Folded to icons only (desktop). */
  collapsed?: boolean;
}

/** One main section; hidden from people without its permission. */
function SidebarNavItem({
  entry,
  onNavigate,
  collapsed = false,
}: { entry: NavEntry; onNavigate?: () => void } & RailProps) {
  const t = useT();
  const allowed = useNavAllowed(entry);
  if (!allowed) return null;
  const Icon = entry.icon;
  const Badge = entry.badge;
  const label = t(entry.labelId);
  const link = (
    <NavLink
      to={entry.to}
      end={entry.end}
      onClick={onNavigate}
      aria-label={collapsed ? label : undefined}
      className={(state) => cn(sidebarRowClass(state), collapsed && "w-11 justify-center px-0")}
    >
      {({ isActive }) => (
        <>
          <span className={sidebarIconTileClass({ isActive })}>
            <Icon aria-hidden="true" />
          </span>
          {collapsed ? null : <span className="min-w-0 flex-1 truncate">{label}</span>}
          {collapsed || !Badge ? null : <Badge />}
        </>
      )}
    </NavLink>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip label={label} side="end">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

/** Fold the sidebar to icons, or open it again. */
function CollapseButton({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const t = useT();
  const label = t(collapsed ? "shell.expandSidebar" : "shell.collapseSidebar");
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <Tooltip label={`${label} (${MOD_KEY}+B)`} side="bottom" align="end">
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={!collapsed}
        className="grid size-9 shrink-0 place-items-center rounded-lg text-ink-subtle transition-colors hover:bg-surface-hover hover:text-ink [&_svg]:size-[18px] rtl:[&_svg]:-scale-x-100"
      >
        <Icon aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

/** Keep wheel movement inside a scrolling nav so the page behind it does not move. A folded
    rail (overflow visible, for tooltips) and a nav that already fits must not steal the wheel,
    or the links scroll away and leave empty space above the account menu. */
function useContainedWheel<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      const scroller = node.querySelector<HTMLElement>("[data-sidebar-nav]") ?? node;
      const next = nextSidebarScrollTop(
        scroller.scrollTop,
        scroller.scrollHeight,
        scroller.clientHeight,
        event.deltaY,
        getComputedStyle(scroller).overflowY,
      );
      if (next === null) return;
      event.preventDefault();
      scroller.scrollTop = next;
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", onWheel);
    };
  }, []);
  return ref;
}

/** The agency mark, and the way home. */
function BrandBlock({
  onNavigate,
  collapsed = false,
  onToggle,
}: { onNavigate?: () => void; onToggle?: () => void } & RailProps) {
  const t = useT();
  // The brand row never changes: folded, it reaches past the rail into the header's place (the
  // header moves aside, see AppShell), so the logo and the toggle stay exactly where they were.
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-1 pt-1 pe-10 pb-3 lg:pe-0",
        collapsed && "ms-2 w-[14.25rem] self-start",
      )}
    >
      <Link
        to="/"
        onClick={onNavigate}
        aria-label={t("app.name")}
        className="min-w-[11.75rem] flex-1 rounded-xl transition-colors hover:bg-surface-hover"
      >
        <LogoWordmark
          className="flex h-12 w-full items-center justify-center px-3 py-0"
          imgClassName="h-8 w-auto max-w-full object-contain"
        />
      </Link>
      {onToggle ? <CollapseButton collapsed={collapsed} onToggle={onToggle} /> : null}
    </div>
  );
}

function QuickSearch({ onSearch }: { onSearch: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onSearch}
      className="group/search mb-4 flex h-11 lg:hidden w-full items-center gap-2.5 rounded-xl border border-line bg-surface ps-3.5 pe-2 text-[15px] text-ink-subtle shadow-xs transition-[border-color,box-shadow] hover:border-line-strong hover:shadow-sm"
    >
      <Search
        className="size-[18px] shrink-0 text-ink-faint transition-colors group-hover/search:text-ink-muted"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 truncate text-start">{t("topbar.search")}</span>
      <span dir="ltr" className="flex shrink-0 gap-0.5">
        <Kbd>{MOD_KEY}</Kbd>
        <Kbd>K</Kbd>
      </span>
    </button>
  );
}

/** The sidebar body, shared by the desktop sidebar and the mobile sheet. */
function SidebarBody({
  collapsed = false,
  onToggle,
  ...actions
}: SidebarActions & RailProps & { onToggle?: () => void }) {
  const t = useT();
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", collapsed && "items-center")}>
      <BrandBlock onNavigate={actions.onNavigate} collapsed={collapsed} onToggle={onToggle} />
      {collapsed ? null : <QuickSearch onSearch={actions.onSearch} />}
      {collapsed ? null : <RunningTimer className="mb-3 shrink-0" />}
      {collapsed ? null : <ClockChip className="mb-3 shrink-0" />}

      <nav
        data-sidebar-nav=""
        aria-label={t("shell.mainNavigation")}
        className={cn(
          "flex min-h-0 flex-1 flex-col overscroll-contain",
          collapsed ? "items-center gap-3" : "gap-5 overflow-y-auto [scrollbar-width:thin]",
        )}
      >
        {NAV_GROUPS.map((group, index) => (
          <div
            key={group.id}
            className={cn("hidden has-[li]:flex flex-col", collapsed && "items-center")}
          >
            {collapsed ? (
              index > 0 ? (
                <hr className="mb-3 w-6 border-line" />
              ) : null
            ) : (
              <h2 className="mb-1.5 px-3 text-[13px] font-semibold text-ink-subtle">
                {t(group.labelId)}
              </h2>
            )}
            <ul className="flex flex-col gap-1">
              {NAV_ENTRIES.filter((entry) => entry.group === group.id).map((entry) => (
                <SidebarNavItem
                  key={entry.id}
                  entry={entry}
                  onNavigate={actions.onNavigate}
                  collapsed={collapsed}
                />
              ))}
            </ul>
          </div>
        ))}
        <div className={cn("hidden has-[li]:flex flex-col", collapsed && "items-center")}>
          {collapsed ? <hr className="mb-3 w-6 border-line" /> : null}
          <ul className="flex flex-col gap-1">
            {NAV_ENTRIES.filter((entry) => entry.group === "footer").map((entry) => (
              <SidebarNavItem
                key={entry.id}
                entry={entry}
                onNavigate={actions.onNavigate}
                collapsed={collapsed}
              />
            ))}
          </ul>
        </div>
      </nav>

      {collapsed ? null : (
        <div className="shrink-0 pt-4">
          <UserMenu onNavigate={actions.onNavigate} />
        </div>
      )}
    </div>
  );
}

interface SidebarProps extends SidebarActions {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle, ...actions }: SidebarProps) {
  const asideRef = useContainedWheel<HTMLElement>();
  return (
    <aside
      ref={asideRef}
      className={cn(
        "sticky top-0 hidden h-dvh min-h-0 shrink-0 flex-col overscroll-contain py-4 transition-[width] duration-200 lg:flex",
        // The folded rail keeps overflow visible so its tooltips can reach past its edge.
        collapsed ? "w-[4.75rem] overflow-visible px-2" : "w-[16rem] overflow-hidden ps-4 pe-3",
      )}
    >
      <SidebarBody {...actions} collapsed={collapsed} onToggle={onToggle} />
    </aside>
  );
}

interface MobileMenuProps extends SidebarActions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The same sidebar as a sheet from the start edge, for screens narrower than the layout. */
export function MobileMenu({ open, onOpenChange, ...actions }: MobileMenuProps) {
  const t = useT();
  const close = () => {
    onOpenChange(false);
  };
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-scrim lg:hidden" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 start-0 z-50 flex w-[20rem] max-w-[88vw] animate-sheet-in flex-col overflow-y-auto border-e border-line bg-canvas ps-5 pe-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-overlay [--sheet-from:-24px] lg:hidden rtl:[--sheet-from:24px]"
        >
          <RadixDialog.Title className="sr-only">{t("shell.mainNavigation")}</RadixDialog.Title>
          <RadixDialog.Close
            aria-label={t("common.close")}
            className="absolute end-3 top-[max(1rem,calc(env(safe-area-inset-top)+0.25rem))] z-10 grid size-8 place-items-center rounded-lg text-ink-subtle transition-colors hover:bg-surface-hover hover:text-ink"
          >
            <X className="size-4" />
          </RadixDialog.Close>
          <SidebarBody
            {...actions}
            onSearch={() => {
              close();
              actions.onSearch();
            }}
            onNewProject={() => {
              close();
              actions.onNewProject();
            }}
            onNavigate={close}
          />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
